import React from "react";
import { Helmet } from "react-helmet-async";

const BlogSEO = ({ 
  post, 
  isDetailPage = false, 
  customTitle, 
  customDescription, 
  customKeywords 
}) => {
  const baseUrl = "https://ekalsutra.com";
  
  // Default blog listing page SEO
  const defaultBlogSEO = {
    title: "Ekalsutra Blog - School Management ERP Insights & Education Technology",
    description: "Read the latest insights on school management ERP software, education technology trends, and best practices for educational institutions. Expert advice and industry updates.",
    keywords: [
      "school management ERP blog",
      "education technology insights", 
      "ERP implementation guide",
      "school administration tips",
      "EdTech trends",
      "digital transformation in education",
      "school ERP software articles",
      "educational technology blog",
      "UP schools digitization",
      "school management system blog",
      "Best practices for school management",
      "school management system blog",
      "best school in india",
      "best school in uttar pradesh",
        "best school in lucknow",
        "best school in delhi", 
        "best school in mumbai",
    ]
  };

  // Blog detail page SEO
  const getDetailPageSEO = (post) => {
    if (!post) return defaultBlogSEO;
    
    return {
      title: customTitle || `${post.title} | Ekalsutra Blog`,
      description: customDescription || post.metaDescription || post.excerpt,
      keywords: customKeywords || post.tags.join(", "),
      canonicalUrl: `${baseUrl}/blog/${post.slug}`,
      publishedTime: post.date,
      modifiedTime: post.date,
      author: post.author,
      category: post.category,
      tags: post.tags
    };
  };

  const seoData = isDetailPage ? getDetailPageSEO(post) : defaultBlogSEO;

  // Structured Data for Blog
  const blogStructuredData = {
    "@context": "https://schema.org",
    "@type": isDetailPage ? "BlogPosting" : "Blog",
    ...(isDetailPage && post ? {
      "headline": post.title,
      "description": post.excerpt,
      "image": `${baseUrl}${post.image}`,
      "author": {
        "@type": "Person",
        "name": post.author,
        "description": post.authorBio
      },
      "publisher": {
        "@type": "Organization",
        "name": "Ekalsutra",
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/landingPage/Ekalsutra_Logo.svg`
        }
      },
      "datePublished": post.date,
      "dateModified": post.date,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${baseUrl}/blog/${post.slug}`
      },
      "articleSection": post.category,
      "keywords": post.tags.join(", "),
      "wordCount": post.content.replace(/<[^>]*>/g, '').split(' ').length
    } : {
      "name": "Ekalsutra Blog",
      "description": defaultBlogSEO.description,
      "url": `${baseUrl}/blog`,
      "publisher": {
        "@type": "Organization",
        "name": "Ekalsutra",
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/landingPage/Ekalsutra_Logo.svg`
        }
      }
    })
  };

  // FAQ Structured Data for blog posts
  const faqStructuredData = isDetailPage && post ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `What is ${post.title.toLowerCase()}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": post.excerpt
        }
      },
      {
        "@type": "Question", 
        "name": "How can Ekalsutra ERP help my school?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ekalsutra ERP provides comprehensive school management solutions including student information systems, fee management, attendance tracking, and parent-teacher communication tools."
        }
      }
    ]
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      <meta name="keywords" content={Array.isArray(seoData.keywords) ? seoData.keywords.join(", ") : seoData.keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={seoData.canonicalUrl || `${baseUrl}/blog`} />
      
      {/* Open Graph Tags */}
      <meta property="og:type" content={isDetailPage ? "article" : "website"} />
      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      <meta property="og:url" content={seoData.canonicalUrl || `${baseUrl}/blog`} />
      <meta property="og:site_name" content="Ekalsutra" />
      {post && <meta property="og:image" content={`${baseUrl}${post.image}`} />}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Article specific Open Graph tags */}
      {isDetailPage && post && (
        <>
          <meta property="article:author" content={seoData.author} />
          <meta property="article:published_time" content={seoData.publishedTime} />
          <meta property="article:modified_time" content={seoData.modifiedTime} />
          <meta property="article:section" content={seoData.category} />
          {seoData.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />
      {post && <meta name="twitter:image" content={`${baseUrl}${post.image}`} />}
      <meta name="twitter:site" content="@ekalsutra" />
      
      {/* Additional SEO Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Language and Location */}
      <meta name="language" content="English" />
      <meta name="geo.region" content="IN-UP" />
      <meta name="geo.placename" content="Uttar Pradesh, India" />
      
      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="format-detection" content="telephone=yes" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(blogStructuredData)}
      </script>
      
      {/* FAQ Structured Data for detail pages */}
      {faqStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      )}
      
      {/* Additional Local SEO for UP/India */}
      <meta name="geo.country" content="IN" />
      <meta name="geo.region" content="UP" />
      <meta name="ICBM" content="26.8467,80.9462" /> {/* Lucknow coordinates */}
      
      {/* Performance and Caching */}
      <meta httpEquiv="cache-control" content="public, max-age=3600" />
      <meta name="referrer" content="no-referrer-when-downgrade" />
    </Helmet>
  );
};

export default BlogSEO;
